import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { getAvailableWarehousesFast } from '@/services/search/fastInventorySearch';

// Session storage key for caching warehouses
const WAREHOUSE_CACHE_KEY = 'inventory_warehouses';
const WAREHOUSE_CACHE_DURATION_MS = 600000; // 10 minutes

interface CachedWarehouses {
  warehouses: { id: number; name: string }[];
  timestamp: number;
}

const getSessionCache = (): CachedWarehouses | null => {
  try {
    const cached = sessionStorage.getItem(WAREHOUSE_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as CachedWarehouses;
      if (Date.now() - parsed.timestamp < WAREHOUSE_CACHE_DURATION_MS) {
        return parsed;
      }
    }
  } catch {
    // Ignore sessionStorage errors
  }
  return null;
};

const setSessionCache = (warehouses: { id: number; name: string }[]) => {
  try {
    const data: CachedWarehouses = { warehouses, timestamp: Date.now() };
    sessionStorage.setItem(WAREHOUSE_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore sessionStorage errors
  }
};

export function useWarehouseSelection() {
  const [availableWarehouses, setAvailableWarehouses] = useState<{ id: number; name: string }[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedRef = useRef(false);

  // Load from session cache on mount
  useEffect(() => {
    const cached = getSessionCache();
    if (cached) {
      console.log('📦 Using cached warehouse list from session storage');
      setAvailableWarehouses(cached.warehouses);
      hasLoadedRef.current = true;
    }
  }, []);

  // Fast warehouse loading with caching
  const loadWarehouses = async () => {
    // Skip if we already have warehouses
    if (hasLoadedRef.current && availableWarehouses.length > 0) {
      console.log('📦 Warehouses already loaded, skipping');
      return availableWarehouses;
    }

    try {
      setIsLoading(true);
      const warehouseIds = await getAvailableWarehousesFast();
      
      // Convert to warehouse objects with names
      const warehouses = warehouseIds.map(id => ({
        id,
        name: `Magazijn ${id}`
      }));
      
      console.log('📦 Available warehouses loaded:', warehouseIds);
      
      if (warehouses.length === 0) {
        toast.warning('Geen magazijnen gevonden');
      }
      
      setAvailableWarehouses(warehouses);
      setSessionCache(warehouses);
      hasLoadedRef.current = true;
      
      return warehouses;
    } catch (error) {
      console.error('Error loading warehouses:', error);
      // Fallback warehouses
      const fallbackWarehouses = [
        { id: 2, name: 'Magazijn 2' },
        { id: 5, name: 'Magazijn 5' },
        { id: 7, name: 'Magazijn 7' },
        { id: 8, name: 'Magazijn 8' },
        { id: 12, name: 'Magazijn 12' }
      ];
      setAvailableWarehouses(fallbackWarehouses);
      return fallbackWarehouses;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle warehouse selection
  const selectWarehouse = (warehouseNumber: number | null) => {
    console.log('Setting warehouse filter to:', warehouseNumber);
    
    if (warehouseNumber !== null) {
      const parsedWarehouse = typeof warehouseNumber === 'string' 
        ? parseInt(warehouseNumber, 10) 
        : warehouseNumber;
        
      if (isNaN(parsedWarehouse)) {
        console.error('Invalid warehouse number:', warehouseNumber);
        setSelectedWarehouse(null);
      } else {
        setSelectedWarehouse(parsedWarehouse);
      }
    } else {
      setSelectedWarehouse(null);
    }
  };

  // Force refresh - clears cache
  const refreshWarehouses = async (): Promise<void> => {
    hasLoadedRef.current = false;
    try {
      sessionStorage.removeItem(WAREHOUSE_CACHE_KEY);
    } catch {
      // Ignore
    }
    await loadWarehouses();
  };

  return {
    availableWarehouses,
    selectedWarehouse,
    selectWarehouse,
    loadWarehouses,
    refreshWarehouses,
    isLoading
  };
}
